/**
 * ThreadHeaderLabel — the "you are here" affordance (thread-workflow UX T2).
 *
 * Renders the ACTIVE thread's title in the chat header's subtitle slot so the
 * user always knows which conversation the pane is showing — in every view
 * (floating / docked / fullscreen), on every page. Click to rename inline
 * (Enter/blur commits, Escape cancels); renaming stamps `userRenamed`, which
 * every labeler/backfill path already respects permanently.
 *
 * The rename is the viewer's OWN name for the thread — nobody else's list
 * changes (PR-B, 2026-09-09) — so it rides `threads.organize-own` (catalog
 * v13). Without the key the label still names the thread and still says which
 * conversation this is; it simply is not a button (PR-1's rule: never a
 * control that fails after the click). `canRename` ABSENT reads as true.
 *
 * THE NAME CAN BE READ (owner report, Miles 2026-09-18). See
 * `threadHeaderTitleReveal.ts` for the full reasoning; in short, on a phone
 * tapping the title used to open the thread list, so a clipped name had no
 * reveal anywhere — and the ☰ in the action rail already opens that list. The
 * tap now shows the whole name, and only when there IS more name to show,
 * measured off the rendered box so a narrow docked pane behaves like a phone
 * without either of them naming a breakpoint.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThreadHeaderLabelButton, ThreadHeaderRenameInput } from './styles';
import {
  isTitleClipped,
  threadHeaderLabelMode,
  threadHeaderLabelText,
} from './threadHeaderTitleReveal';

export interface ThreadHeaderLabelProps {
  title: string;
  onRename: (title: string) => void;
  /** Whether this viewer may rename a thread for themselves; absent ⇒ yes. */
  canRename?: boolean;
  /**
   * Phones (2026-09-13, re-aimed 2026-09-18): the tap shows the whole thread
   * name rather than opening an inline rename.
   *
   * The chip strip is retired at phone width — a horizontally-scrolling rail
   * that showed about one and a half chips on a 390px screen, for ~48px of a
   * transcript already getting less than half. Thread switching moved into the
   * header, and rename moved into the ThreadListSheet (search, new thread and
   * per-row rename included), which is where it still lives: the surface goes,
   * every affordance it carried stays reachable.
   *
   * What changed on 2026-09-18 is only WHICH header control switches threads.
   * The title opened the list too, which meant the largest, most obvious target
   * for "what is this thread called" answered a different question and the
   * clamped name stayed unreadable. The ☰ beside it — first in the rail, the
   * one control here that can carry the unread badge — keeps that job alone.
   *
   * Absent ⇒ click renames, exactly as before, at every other width.
   */
  revealFullTitleOnTap?: boolean;
}

export const ThreadHeaderLabel: React.FC<ThreadHeaderLabelProps> = ({
  title,
  onRename,
  canRename,
  revealFullTitleOnTap,
}) => {
  const mayRename = canRename !== false;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const textRef = useRef<HTMLSpanElement | null>(null);

  // Track external title changes (AI label landing, cross-device rename)
  // while NOT clobbering an in-progress edit.
  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  // A new thread is a new question about whether its name fits, and an
  // expansion belongs to the title that was expanded — carrying it across a
  // thread switch would open the next thread with its name blown out.
  useEffect(() => {
    setExpanded(false);
  }, [title]);

  /**
   * Re-measure whenever the box could have changed: the title, a rotation, a
   * docked pane drag, the font landing. `useLayoutEffect` so the first paint
   * already knows — otherwise the label flickers from span to button.
   *
   * ResizeObserver is guarded rather than assumed: jsdom does not ship one,
   * and a header that throws is a chat that does not render.
   */
  const measure = useCallback(() => {
    setClipped(isTitleClipped(textRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!revealFullTitleOnTap) return undefined;
    measure();
    const el = textRef.current;
    const RO = typeof window !== 'undefined'
      ? (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver
      : undefined;
    if (!el || !RO) return undefined;
    const observer = new RO(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
    // `expanded` is a dependency on purpose: expanding removes the clamp, so
    // the box the observer watches is a different box and must be re-read —
    // without it, collapsing could leave `clipped` false and the reveal gone.
  }, [revealFullTitleOnTap, title, expanded, measure]);

  const commit = () => {
    const clean = draft.trim();
    setEditing(false);
    if (clean && clean !== title) onRename(clean);
  };

  if (editing && mayRename && !revealFullTitleOnTap) {
    return (
      <ThreadHeaderRenameInput
        value={draft}
        autoFocus
        data-thread-header-rename=""
        aria-label="rename this thread"
        maxLength={42}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(title);
            setEditing(false);
          }
        }}
      />
    );
  }

  const mode = threadHeaderLabelMode({
    revealOnTap: revealFullTitleOnTap === true,
    clipped,
    expanded,
    canRename: mayRename,
  });
  const { ariaLabel, tooltip } = threadHeaderLabelText(title, mode);

  // Nothing to reveal and no rename to offer: a plain, non-interactive label.
  if (mode === 'static') {
    return (
      <ThreadHeaderLabelButton
        as="span"
        data-thread-header-label=""
        // The expansion survives a re-measure that says the name now fits: the
        // wrapped text IS what makes it fit, so dropping the attribute here
        // would snap the name shut the instant it became readable.
        data-thread-header-expanded={expanded ? '' : undefined}
        title={tooltip}
        aria-label={ariaLabel}
      >
        <span data-thread-header-title="" ref={textRef}>{title}</span>
      </ThreadHeaderLabelButton>
    );
  }

  const onClick = mode === 'rename'
    ? () => setEditing(true)
    : () => setExpanded((v) => !v);

  return (
    <ThreadHeaderLabelButton
      type="button"
      data-thread-header-label=""
      data-thread-header-expanded={expanded ? '' : undefined}
      {...(mode === 'rename' ? {} : { 'aria-expanded': expanded })}
      title={tooltip}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      <span data-thread-header-title="" ref={textRef}>{title}</span>
    </ThreadHeaderLabelButton>
  );
};

export default ThreadHeaderLabel;
